import { Component } from '@theme/component';

/**
 * PITH desktop thumbnail rail for the PDP media gallery (grid presentation).
 *
 * Reads the sibling `<media-gallery>` grid items directly from the DOM instead of
 * re-deriving the media/variant sort order — that keeps it byte-for-byte in sync with
 * whatever Horizon actually rendered. Horizon replaces the whole `<media-gallery>`
 * element on variant change (see media-gallery.js `#handleVariantUpdate`), so a plain
 * MutationObserver on the shared parent — rather than an event listener racing that
 * replacement — is what catches the swap and triggers a rebuild.
 *
 * @extends {Component}
 */
export class PdpGalleryThumbnails extends Component {
  /** @type {HTMLElement | null} */
  #container = null;
  /** @type {MutationObserver | null} */
  #parentObserver = null;
  /** @type {IntersectionObserver | null} */
  #visibilityObserver = null;
  /** @type {Element[]} */
  #items = [];

  connectedCallback() {
    super.connectedCallback();

    this.#container = this.closest('.product-information__media');
    if (!this.#container) return;

    this.#build();

    this.#parentObserver = new MutationObserver(() => this.#build());
    this.#parentObserver.observe(this.#container, { childList: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#parentObserver?.disconnect();
    this.#visibilityObserver?.disconnect();
  }

  /**
   * Selects (scrolls to) the media item at the given index.
   * @param {number} index
   */
  select(index) {
    this.#items[index]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  #build() {
    this.#visibilityObserver?.disconnect();

    const gallery = this.#container?.querySelector('media-gallery');
    const grid = gallery?.dataset.presentation === 'grid' ? gallery.querySelector('.media-gallery__grid') : null;
    const items = grid ? Array.from(grid.children) : [];

    if (items.length < 2) {
      this.#items = [];
      this.replaceChildren();
      return;
    }

    this.#items = items;

    const buttons = items.map((item, index) => this.#buildThumbnail(item, index, items.length));
    this.replaceChildren(...buttons);
    this.#observeVisibility(items, buttons);
  }

  /**
   * @param {Element} item
   * @param {number} index
   * @param {number} total
   * @returns {HTMLButtonElement}
   */
  #buildThumbnail(item, index, total) {
    const sourceImage = item.querySelector('.product-media__image');

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'button button-unstyled ds-gallery-thumbnails__thumbnail';
    button.setAttribute('aria-label', `View image ${index + 1} of ${total}`);
    button.setAttribute('on:click', `/select/${index}`);

    if (sourceImage instanceof HTMLImageElement) {
      const image = /** @type {HTMLImageElement} */ (sourceImage.cloneNode(true));
      image.removeAttribute('ref');
      image.removeAttribute('fetchpriority');
      image.sizes = '64px';
      image.loading = 'lazy';
      button.append(image);
    }

    return button;
  }

  /**
   * @param {Element[]} items
   * @param {HTMLButtonElement[]} buttons
   */
  #observeVisibility(items, buttons) {
    this.#visibilityObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const index = items.indexOf(entry.target);
          if (index === -1) continue;

          if (entry.isIntersecting) buttons[index].setAttribute('aria-selected', 'true');
          else buttons[index].removeAttribute('aria-selected');
        }
      },
      { rootMargin: '-45% 0px -45% 0px', threshold: 0 }
    );

    for (const item of items) this.#visibilityObserver.observe(item);
  }
}

if (!customElements.get('pdp-gallery-thumbnails')) {
  customElements.define('pdp-gallery-thumbnails', PdpGalleryThumbnails);
}
